import React, { memo, useCallback, useMemo } from "react";

import {
  FlamebearerCall,
  FlamebearerNode,
  ProfileType
} from "@platformsh/observability-graph/ContinuousProfiling";

import Dialog from "ds/Dialog";

import FlamebearerCallTooltip from "../FlamebearerCallTooltip";

import * as S from "./styles";

type Props = {
  node: FlamebearerNode;
  range: [number, number];
  profileType: ProfileType;

  truncated: boolean;
  onNodeSelected: (call: FlamebearerNode | null) => void;

  highlighted: boolean;
  onCallHighlighted: (call: FlamebearerCall | null) => void;
};

const FlamebearerGraphNode = ({
  node,
  range,
  profileType,
  truncated,
  onNodeSelected,
  highlighted,
  onCallHighlighted
}: Props) => {
  const handleEnter = useCallback(() => {
    onCallHighlighted(node.call);
  }, [node.call, onCallHighlighted]);
  const handleLeave = useCallback(() => {
    onCallHighlighted(null);
  }, [onCallHighlighted]);
  const handleClick = useCallback(() => {
    onNodeSelected(node);
  }, [node, onNodeSelected]);

  const [left, right] = useMemo(() => {
    const size = range[1] - range[0];
    const left = Math.max(0, node.start - range[0]);
    const right = Math.max(0, range[1] - node.end);
    return [left / size, right / size];
  }, [node.end, node.start, range]);

  return (
    <Dialog
      align="center"
      trigger={
        <S.Node
          level={node.level}
          highlighted={highlighted}
          truncated={truncated}
          style={{
            left: `${100 * left}%`,
            right: `${100 * right}%`
          }}
          onClick={handleClick}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <S.NodeBackground style={{ opacity: node.exclusive.impact }} />
          <S.NodeLabel>{node.call.label}</S.NodeLabel>
        </S.Node>
      }
      link={
        <FlamebearerCallTooltip callMetrics={node} profileType={profileType} />
      }
    />
  );
};

export default memo(FlamebearerGraphNode);
